import cn from 'classnames';
import { FC, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import { Button, Modal } from '@/shared/ui';

import { authModel } from '@/entities/auth';

import { MFA_CODE_LENGTH } from '../../config';
import { useMFAErrorMessage } from '../../hooks';
import { MFACodeField } from '../MFACodeField/MFACodeField';

import styles from './DisableMFAModal.module.scss';

type DisableMFAModalProps = {
  isOpen: boolean;
  className?: string;
  onCancel: () => void;
  onDisabled: () => void;
};

/**
 * Turning the second factor off asks for a current code, not a password: a
 * hijacked session already holds the password.
 */
export const DisableMFAModal: FC<DisableMFAModalProps> = (props) => {
  const { isOpen, className, onCancel, onDisabled } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const getErrorMessage = useMFAErrorMessage('disable');

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isDisabling, setIsDisabling] = useState(false);

  // A ref, not the state flag: a click can land in the same tick as the
  // auto-submit on the sixth digit.
  const isDisablingRef = useRef(false);

  const close = () => {
    setCode('');
    setErrorMessage('');
    onCancel();
  };

  const disable = async (currentCode: string) => {
    if (currentCode.length !== MFA_CODE_LENGTH || isDisablingRef.current) {
      return;
    }

    isDisablingRef.current = true;
    setIsDisabling(true);

    try {
      await dispatch(
        authModel.thunks.mfaDisable({ CurrentCode: currentCode }),
      ).unwrap();

      setCode('');
      onDisabled();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setCode('');
    } finally {
      isDisablingRef.current = false;
      setIsDisabling(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      className={className}
      onCancel={close}
      dataSection="mfa-disable-modal"
      title={formatMessage({
        id: 'mfa.disable.title',
        defaultMessage: 'Disable two-step verification',
      })}
      footer={
        <div className={styles.footer}>
          <Button
            variant="secondary"
            danger
            disabled={isDisabling}
            onClick={close}
          >
            <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
          </Button>

          <Button
            danger
            loading={isDisabling}
            disabled={code.length !== MFA_CODE_LENGTH}
            onClick={() => disable(code)}
          >
            <FormattedMessage id="global.disable" defaultMessage="Disable" />
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <p className={cn(styles.description, 'p2')}>
          <FormattedMessage
            id="mfa.disable.description"
            defaultMessage="Your account will be protected by the password alone. Enter the current code from your authenticator app to confirm."
          />
        </p>

        <MFACodeField
          value={code}
          autoFocus
          disabled={isDisabling}
          errorMessage={errorMessage}
          onChange={(nextCode) => {
            setCode(nextCode);
            setErrorMessage('');
          }}
          onComplete={disable}
          label={
            <FormattedMessage
              id="mfa.setup.codeLabel"
              defaultMessage="Code from the app"
            />
          }
        />
      </div>
    </Modal>
  );
};
